// src/pages/Exams/generate/QuestionCard.tsx
import { useState } from 'react';
import type { QuestionDraft, QuestionFlag, RegenVariant } from './types';
import { MathText } from './MathText';
import { Badge, Button, Chip, TextLinkButton, Term } from '../../../school_panel/components/ui';
import { Check, ChevronDown, Pencil, RotateCw, X } from 'lucide-react';

interface Props {
  question: QuestionDraft;
  index: number;
  /** Chapter name for the chip; omitted when the chapter list has not loaded. */
  chapterName?: string;
  previewMode?: boolean;
  selected?: boolean;
  highlight?: boolean;
  onToggleSelect?: (index: number) => void;
  onUpdate?: (index: number, patch: Partial<QuestionDraft>) => void;
  onRemove?: (index: number) => void;
  onRegenerate?: (index: number, variant: RegenVariant) => Promise<unknown>;
  onToggleFlag?: (index: number, flag: QuestionFlag) => void;
}

const ANS_TO_INDEX: Record<string, number> = { A: 0, B: 1, C: 2, D: 3 };
const VARIANTS: Array<{ value: RegenVariant; label: string }> = [
  { value: 'rephrase', label: 'Rephrase' },
  { value: 'harder', label: 'Make harder' },
  { value: 'easier', label: 'Make easier' },
  { value: 'regenerate', label: 'Fresh question' },
];
const ALL_FLAGS: QuestionFlag[] = [
  'ambiguous_question', 'choice_ambiguity', 'weak_distractors',
  'reference_mismatch', 'multiple_correct',
];

const INPUT_CLASS =
  'min-h-[44px] w-full rounded-[12px] border border-line-strong bg-surface px-3 py-2 text-sm text-ink ' +
  'focus:outline-2 focus:outline-offset-1 focus:outline-accent';

const MENU_CLASS =
  'absolute z-10 mt-1 overflow-hidden rounded-[12px] border border-line bg-surface shadow-card';

const MENU_ITEM_CLASS =
  'block min-h-[36px] w-full px-3 py-2 text-left text-xs text-ink hover:bg-surface-2';

// Design Q-score chip: green from 70, amber from 50, red below.
function qualityChip(score: number | null) {
  if (score == null) return <Badge tone="neutral">Q-score —</Badge>;
  if (score >= 70) return <Chip tone="up">Q-score {score}</Chip>;
  if (score >= 50) return <Badge tone="warning">Q-score {score}</Badge>;
  return <Chip tone="down">Q-score {score}</Chip>;
}

const DIFF_TONE: Record<string, 'success' | 'info' | 'accent'> = { easy: 'success', medium: 'info', hard: 'accent' };

export function QuestionCard({
  question, index, chapterName, previewMode, selected, highlight, onToggleSelect,
  onUpdate, onRemove, onRegenerate, onToggleFlag,
}: Props) {
  const [showExplain, setShowExplain] = useState(false);
  const [editing, setEditing] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const editable = !!onUpdate && !previewMode;
  const correctIdx = ANS_TO_INDEX[(question.ans || '').toUpperCase()] ?? -1;
  const choices = [question.choice_a, question.choice_b, question.choice_c, question.choice_d];
  const choiceKeys: Array<keyof QuestionDraft> = ['choice_a', 'choice_b', 'choice_c', 'choice_d'];

  const runRegen = async (variant: RegenVariant) => {
    if (!onRegenerate) return;
    setRegenOpen(false);
    setBusy(true);
    // Called un-awaited from onClick; catch so a failed regenerate logs instead
    // of surfacing as an unhandled rejection (parity with template/retry guards).
    try { await onRegenerate(index, variant); }
    catch (e) { console.error('[exam_gen] regenerate failed', e); }
    finally { setBusy(false); }
  };

  const diff = String(question.difficulty || '').toLowerCase();

  return (
    <article
      aria-label={`Question ${index + 1}`}
      className={`mb-3 rounded-[18px] border bg-surface px-5 py-[18px] shadow-qc transition-shadow duration-1000 ${
        selected ? 'border-accent ring-1 ring-accent' : 'border-line'
      }${highlight ? ' ring-2 ring-accent' : ''}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {onToggleSelect && !previewMode && (
          <input type="checkbox" checked={!!selected} className="h-4 w-4 accent-accent"
            onChange={() => onToggleSelect(index)} aria-label={`select question ${index + 1}`} />
        )}
        <span className="font-mono text-[13px] font-semibold text-ink">Q{index + 1}</span>
        {!previewMode && (
          <>
            {chapterName && (
              <span title={chapterName} className="max-w-[16rem] truncate text-[13px] text-muted">{chapterName}</span>
            )}
            <span className="flex-1" />
            {diff && <Badge tone={DIFF_TONE[diff] ?? 'neutral'}>{question.difficulty}</Badge>}
            <Term k="bloom" plain><Badge tone="info">{question.bloom_level || 'understand'}</Badge></Term>
            <Term k="quality" plain>{qualityChip(question.quality_score)}</Term>
            {question.flags?.length > 0 && question.flags.map(f => (
              onToggleFlag ? (
                <button key={f} type="button" aria-label={f}
                  onClick={() => onToggleFlag(index, f as QuestionFlag)}
                  className="min-h-[36px] hover:line-through">
                  <Badge tone="warning">{f}<X size={14} strokeWidth={1.8} aria-hidden className="ml-1" /></Badge>
                </button>
              ) : (
                <Badge key={f} tone="warning">{f}</Badge>
              )
            ))}
          </>
        )}
      </div>

      {editing && editable ? (
        <textarea
          className={`mb-3 ${INPUT_CLASS}`}
          aria-label="Question"
          rows={3} value={question.question}
          onChange={(e) => onUpdate!(index, { question: e.target.value })}
        />
      ) : (
        <p className="mb-3.5 whitespace-pre-wrap text-[15.5px] font-medium leading-[1.5] text-ink">
          <MathText>{question.question}</MathText>
        </p>
      )}

      <ul className="m-0 grid list-none grid-cols-1 gap-2 p-0 min-[821px]:grid-cols-2">
        {choices.map((text, i) => {
          const isCorrect = !previewMode && i === correctIdx;
          return (
            <li key={i}
              className={`flex items-center gap-2.5 rounded-[12px] border px-3 py-[9px] text-sm text-ink ${
                isCorrect ? 'border-success-line bg-success-soft font-semibold' : 'border-line'}`}>
              <span aria-hidden className={`grid h-6 w-6 flex-none place-items-center rounded-[8px] font-mono text-xs font-semibold ${
                isCorrect ? 'bg-success text-white' : 'bg-track text-muted-2'}`}>{String.fromCharCode(65 + i)}</span>
              <span className="min-w-0 flex-1">
                {editing && editable ? (
                  <input className={INPUT_CLASS}
                    aria-label={`Choice ${String.fromCharCode(65 + i)}`}
                    value={text}
                    onChange={(e) => onUpdate!(index, { [choiceKeys[i]]: e.target.value } as Partial<QuestionDraft>)} />
                ) : <MathText>{text}</MathText>}
              </span>
              {isCorrect && (
                <span role="img" aria-label="Correct answer" className="flex-none">
                  <Chip tone="up"><Check size={14} strokeWidth={2} aria-hidden />Correct</Chip>
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {editing && editable && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted">Correct answer:</span>
          {['A', 'B', 'C', 'D'].map(letter => {
            const active = (question.ans || '').toUpperCase() === letter;
            return (
              <button key={letter} type="button"
                aria-pressed={active}
                onClick={() => onUpdate!(index, { ans: letter })}
                className={`min-h-[36px] rounded-[10px] border px-3 py-1 font-semibold transition-colors ${
                  active
                    ? 'border-accent bg-accent-bg text-accent-ink'
                    : 'border-line text-ink hover:bg-surface-2'}`}>
                {letter}
              </button>
            );
          })}
        </div>
      )}

      {!previewMode && showExplain && (
        editing && editable ? (
          <textarea className={`mt-3 ${INPUT_CLASS}`}
            aria-label="Explanation"
            rows={3} value={question.explanation}
            onChange={(e) => onUpdate!(index, { explanation: e.target.value })} />
        ) : (
          <div className="mt-3 rounded-[12px] bg-ground px-3.5 py-3 text-[13.5px] text-ink">
            <span className="mb-1 block font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Explanation</span>
            {question.explanation || '(no explanation)'}
            {question.reference && <div className="mt-1 text-muted">Ref: {question.reference}</div>}
          </div>
        )
      )}

      {!previewMode && regenOpen && onRegenerate && (
        <div role="group" aria-label="Regenerate options" className="mt-2.5 flex flex-wrap gap-1.5 rounded-[12px] bg-panel p-2">
          {VARIANTS.map(v => (
            <Button key={v.value} variant="ghost" size="xs" onClick={() => runRegen(v.value)}>{v.label}</Button>
          ))}
        </div>
      )}

      {!previewMode && (
        <div className="mt-3.5 flex flex-wrap items-center gap-2 border-t border-hairline pt-3.5">
          {editable && (
            <Button variant="ghost" size="xs" icon={<Pencil size={14} strokeWidth={1.8} aria-hidden />} aria-pressed={editing}
              onClick={() => setEditing(e => !e)}>
              {editing ? 'Done editing' : 'Edit'}
            </Button>
          )}
          {onRegenerate && (
            <Button variant="ghost" size="xs" loading={busy} aria-expanded={regenOpen}
              onClick={() => setRegenOpen(o => !o)}
              icon={<RotateCw size={14} strokeWidth={1.8} aria-hidden />}>Regenerate</Button>
          )}
          {editable && onToggleFlag && (
            <div className="relative">
              <Button variant="ghost" size="xs" onClick={() => setFlagOpen(o => !o)}
                icon={<ChevronDown size={14} strokeWidth={1.8} aria-hidden />}>Flag</Button>
              {flagOpen && (
                <div className={`${MENU_CLASS} w-48`}>
                  {ALL_FLAGS.map(f => (
                    <button key={f} type="button"
                      onClick={() => { onToggleFlag(index, f); setFlagOpen(false); }}
                      className={MENU_ITEM_CLASS}>
                      {question.flags?.includes(f) ? '✓ ' : ''}{f}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {onRemove && (
            <Button variant="ghost" size="xs" onClick={() => onRemove(index)}>Remove</Button>
          )}
          <span className="flex-1" />
          <TextLinkButton aria-expanded={showExplain} onClick={() => setShowExplain(s => !s)}>
            {showExplain ? 'Hide explanation' : 'Show explanation'}
          </TextLinkButton>
        </div>
      )}
    </article>
  );
}
